import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class DeviceFingerprinterService {

  constructor(private platform: Platform) { }

  async generateFingerprint(): Promise<string> {
    const components = await this.gatherDeviceComponents();
    return this.hashComponents(components);
  }

  private async gatherDeviceComponents(): Promise<Record<string, any>> {
    const deviceInfo = await Device.getInfo();
    const screenInfo = this.getScreenInfo();
    const browserInfo = this.getBrowserInfo();
    const hardwareInfo = this.getHardwareInfo();
    const networkInfo = await this.getNetworkInfo();

    return {
      // Device specific information
      model: deviceInfo.model,
      platform: deviceInfo.platform,
      operatingSystem: deviceInfo.operatingSystem,
      osVersion: deviceInfo.osVersion,
      manufacturer: deviceInfo.manufacturer,
      isVirtual: deviceInfo.isVirtual,
      webViewVersion: deviceInfo.webViewVersion,

      // Screen properties
      ...screenInfo,

      // Browser/Environment information
      ...browserInfo,

      // Hardware capabilities
      ...hardwareInfo,

      // Network information
      ...networkInfo,

      // Time and locale information
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      timeZoneOffset: new Date().getTimezoneOffset(),
    };
  }

  private getScreenInfo() {
    return {
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      screenDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      screenOrientation: screen.orientation.type,
    };
  }

  private getBrowserInfo() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');

    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency,
      webglVendor: gl?.getParameter(gl.VENDOR),
      webglRenderer: gl?.getParameter(gl.RENDERER),
    };
  }

  private getHardwareInfo() {
    return {
      maxTouchPoints: navigator.maxTouchPoints,
      deviceMemory: (navigator as any).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
    };
  }

  private async getNetworkInfo() {
    const connection = (navigator as any).connection;

    return {
      connectionType: connection?.type,
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
      rtt: connection?.rtt,
    };
  }

  private hashComponents(components: Record<string, any>): string {
    const sortedComponents = Object.keys(components)
      .sort()
      .reduce((acc, key) => {
        acc[key] = components[key];
        return acc;
      }, {} as Record<string, any>);
  
    const stringToHash = JSON.stringify(sortedComponents);
    return this.simpleHash(stringToHash);
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  async getFingerprintEntropy(): Promise<{
    components: Record<string, any>;
    entropyBits: number;
  }> {
    const components = await this.gatherDeviceComponents();

    let totalEntropy = 0;
    for (const [key, value] of Object.entries(components)) {
      if (value !== undefined && value !== null) {
        const componentEntropy = this.calculateComponentEntropy(value);
        totalEntropy += componentEntropy;
      }
    }

    return {
      components,
      entropyBits: totalEntropy,
    };
  }

  private calculateComponentEntropy(value: any): number {
    const stringValue = String(value);
    return Math.log2(stringValue.length + 1);
  }
}
